import { TAddOn, TPlan } from "@/components/Checkout/types.checkout";
import {
  ADDON_REF_ID_MAPPING,
  PLAN_REF_ID_MAPPING,
  PLANS_IN_BUNDLE,
} from "@/data/plans";
import { TBillingInterval } from "@/types/app";
import { TProductPricing } from "@/types/plan";
import {
  getIntervalPrice,
  getIntervalStrikePrice,
} from "@/utils/billingInterval";
import {
  TAddOnCartItem,
  TAddOnType,
  TCartItem,
  TCartItemActions,
  TCatalogPlan,
  TManagedCartItem,
} from "./types.cart";

function getCartItemPrice(
  pricing: TProductPricing,
  billingPeriod: TBillingInterval,
): number | undefined {
  return getIntervalPrice(pricing, billingPeriod);
}

/** Maps an add-on's productRefId to its specific cart item add-on type. */
export function getAddOnType(productRefId: TAddOn["productRefId"]): TAddOnType {
  if (productRefId === ADDON_REF_ID_MAPPING.gallery_unlimited)
    return "add-on-gallery-unlimited";
  return "add-on-profile";
}

export function createCartItemFromCard(
  card: TPlan | TAddOn,
  billingPeriod: TBillingInterval,
  type: "plan" | "add-on",
  quantity: number = 1,
): TCartItem {
  const price = getCartItemPrice(card.pricing, billingPeriod);
  const listPrice = getIntervalStrikePrice(card.pricing, billingPeriod);

  if (type === "plan") {
    const planCard = card as TPlan;

    return {
      id: planCard.productRefId,
      name: planCard.planName,
      price,
      listPrice,
      quantity,
      interval: billingPeriod,
      type,
    };
  }

  const addOnCard = card as TAddOn;

  return {
    id: addOnCard.productRefId,
    name: addOnCard.name,
    price,
    listPrice,
    quantity,
    interval: billingPeriod,
    type: getAddOnType(addOnCard.productRefId),
  };
}

export function createBundleItem(
  catalogPlans: TCatalogPlan[],
  interval: TBillingInterval,
): TCartItem | null {
  const bundlePlan = catalogPlans.find((p) => p.id === PLAN_REF_ID_MAPPING.all);
  if (!bundlePlan) return null;
  return {
    id: PLAN_REF_ID_MAPPING.all,
    name: bundlePlan.name,
    price: getIntervalPrice(bundlePlan.pricing, interval),
    listPrice: getIntervalStrikePrice(bundlePlan.pricing, interval),
    quantity: 1,
    interval,
    type: "plan",
  };
}

// ── Per-item decoration ──

/**
 * Attach per-item action handlers to a cart item. `build` returns the actions
 * for the given item (or `undefined` to leave it bare).
 */
export function addCartItemAction<T extends TCartItem>(
  item: T,
  build: (item: T) => TCartItemActions | undefined,
): TManagedCartItem<T> {
  const actions = build(item);
  return actions ? { ...item, actions } : item;
}

// ── Cart filtering ──

export function filterPlans(cart: TCartItem[]): TCartItem[] {
  return cart.filter((i) => i.type === "plan");
}

export function filterAddOns(cart: TCartItem[]): TAddOnCartItem[] {
  return cart.filter((i): i is TAddOnCartItem => i.type !== "plan");
}

// ── Cart ID set (with bundle expansion) ──

export function getCartIdSet(cart: TCartItem[]): Set<string> {
  const ids = new Set<string>(cart.map((i) => i.id as string));
  if (ids.has(PLAN_REF_ID_MAPPING.all)) {
    (PLANS_IN_BUNDLE as readonly string[]).forEach((id) => ids.add(id));
  }
  return ids;
}
